import { type Dispatch, type FC, type SetStateAction, useMemo } from 'react'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalSidebar } from '@/components/commons/gallery-modal-sidebar'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { GalleryMetadata } from '@/features/photoset/components/gallery-photoset-modal/gallery-metadata/gallery-metadata'
import { useGalleryCurationShortcuts } from '@/features/photoset/hooks/use-gallery-curation-shortcuts'
import type { Image } from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

interface GalleryShowcaseModal {
	selectableImages: Omit<Image, 'space'>[]
	currentDetailImageIndex: number
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
	onClose: () => void
}

export const GalleryShowcaseModal: FC<GalleryShowcaseModal> = ({
	selectableImages,
	currentDetailImageIndex,
	setCurrentDetailImageIndex,
	onClose
}) => {
	const { t } = useTranslation()

	const currentImage = useMemo(
		() => selectableImages[currentDetailImageIndex],
		[currentDetailImageIndex, selectableImages]
	)

	useGalleryCurationShortcuts({
		currentDetailImageIndex,
		currentImage,
		setCurrentDetailImageIndex,
		selectableImages,
		currentSpaceIndex: null,
		onClose
	})

	return (
		<GalleryModal
			currentImage={currentImage}
			headerSlot={
				<GalleryModalHeader
					currentImage={currentImage}
					imagesCount={selectableImages.length}
					onClose={onClose}
				/>
			}
			onClose={onClose}
			sidebarSlot={
				<GalleryModalSidebar
					content={
						<GalleryMetadata
							imageMetadata={currentImage.metadata}
						/>
					}
					title={t('photoset:curation.sidebar_title')}
				/>
			}
			thumbnails={currentImage.thumbnails}
			thumbnailsSlot={
				<GalleryModalThumbnails
					currentImageId={currentImage.id}
					images={selectableImages}
					onSelect={(_, index) => {
						setCurrentDetailImageIndex(index)
					}}
					thumbnails={'thumbnails'}
				/>
			}
		/>
	)
}
